import { mapActions, mapGetters } from 'vuex';
import { CHATBOT_COMMANDS } from '~/utils/chat-flags';
import { getInvoices } from '~/api/invoices';
import { getMitraSendMoneyTransaction } from '~/api/mitra-send-money';
import { updateDigitalSendTransaction } from '~/api/mitra-lakupandai';
import { humanizeTimestamp } from '~/utils/date-helper';

export const DIGITAL_SEND_MAINTENANCE_NEO_TOGGLE_KEY = 'payment/digital-send/maintenance';

export default {
  computed: {
    ...mapGetters({
      getNeoToggles: 'neo/getNeoToggles',
    }),
  },
  methods: {
    ...mapActions({
      fetchNeoToggles: 'neo/fetchNeoToggles',
    }),

    /**
     * Check status of kirim uang/digital-send transactions
     * @command - [apicall] checkKirimUangStatus [/apicall]
     * @param {object} currentTrx - currently selected transaction object
     */
    async checkKirimUangStatus(currentTrx = null) {
      if (!currentTrx) {
        this.$zopim.sendChatMsg(CHATBOT_COMMANDS.responses.noSelectedTransaction);
        return;
      }

      /**
       * Possible responses if status exists
       * [apiresponse] kirimUangStatusPending [/apiresponse]  // this won't happen though, because pending transactions are not shown in Mitra transaction list
       * [apiresponse] kirimUangStatusInvoiced [/apiresponse]  // this won't happen though, because invoiced transactions are not shown in Mitra transaction list
       * [apiresponse] kirimUangStatusProcessed [/apiresponse]
       * [apiresponse] kirimUangStatusRemitted [/apiresponse]
       * [apiresponse] kirimUangStatusRefunded [/apiresponse]
       */
      const responses = {
        kirimUangStatus: '[apiresponse] kirimUangStatus<status> [/apiresponse]',
        kirimUangStatusIsError: '[apiresponse] kirimUangStatusError [/apiresponse]',
        transactionIsNotKirimUang: '[apiresponse] transactionIsNotKirimUang [/apiresponse]',
        kirimUangFeatureIsInMaintenance: '[apiresponse] kirimUangFeatureIsInMaintenance [/apiresponse]',
      };

      try {
        /**
         * Checks if the Kirim Uang/Digital Send feature is in maintenance mode
         */
        await this.fetchNeoToggles([DIGITAL_SEND_MAINTENANCE_NEO_TOGGLE_KEY]);
        if (this.getNeoToggles[DIGITAL_SEND_MAINTENANCE_NEO_TOGGLE_KEY]) {
          this.$zopim.sendChatMsg(responses.kirimUangFeatureIsInMaintenance);
          return;
        }

        // Get invoice data
        const {
          data: [invoiceData],
        } = await getInvoices(this.$axios, { keywords: currentTrx.paymentId });
        const {
          transactions: [invoiceTrx],
        } = invoiceData;

        if (invoiceTrx.type !== 'digital-send') {
          this.$zopim.sendChatMsg(responses.transactionIsNotKirimUang);
          return;
        }

        const { data: sendMoneyData } = await getMitraSendMoneyTransaction(this.$axios, invoiceTrx.id);

        if (!sendMoneyData || !sendMoneyData.status) {
          this.$zopim.sendChatMsg(responses.kirimUangStatusIsError);
          return;
        }

        const { status } = sendMoneyData;
        const capitalizedState = status.charAt(0).toUpperCase() + status.slice(1);
        const finalResponse = responses.kirimUangStatus.replace('<status>', capitalizedState);
        this.$zopim.sendChatMsg(finalResponse);
      } catch (error) {
        this.$zopim.sendChatMsg(responses.kirimUangStatusIsError);
        console.error('[ERROR] Something is wrong when trying to check auto refund eligibilty', error);
      }
    },

    /**
     *  Check remitted time of transaction with type 'digital-send'
     * @command - '[apicall] checkKirimUangRemittedTime [/apicall]'
     * @param {object} currentTrx - currently selected transaction object
     */
    async checkKirimUangRemittedTime(currentTrx) {
      if (!currentTrx) {
        this.$zopim.sendChatMsg(CHATBOT_COMMANDS.responses.noSelectedTransaction);
        return;
      }

      const responses = {
        remittedAbove5Days: '[apiresponse] remittedAbove5Days [/apiresponse]',
        remittedBelow5Days: '[apiresponse] remittedBelow5Days [/apiresponse]',
        transactionIsNotKirimUang: '[apiresponse] transactionIsNotKirimUang [/apiresponse]',
        kirimUangStatusIsNotRemitted: '[apiresponse] kirimUangStatusIsNotRemitted [/apiresponse]',
        kirimUangFeatureIsInMaintenance: '[apiresponse] kirimUangFeatureIsInMaintenance [/apiresponse]',
        error: '[apiresponse] checkKirimUangRemittedTimeError [/apiresponse]',
      };

      try {
        /**
         * Check if kirim uang feature is in Maintenance mode
         */
        await this.fetchNeoToggles([DIGITAL_SEND_MAINTENANCE_NEO_TOGGLE_KEY]);
        if (this.getNeoToggles[DIGITAL_SEND_MAINTENANCE_NEO_TOGGLE_KEY]) {
          this.$zopim.sendChatMsg(responses.kirimUangFeatureIsInMaintenance);
          return;
        }

        // Get invoice data
        const {
          data: [invoiceData],
        } = await getInvoices(this.$axios, { keywords: currentTrx.paymentId });
        const {
          transactions: [invoiceTrx],
        } = invoiceData;

        if (invoiceTrx.type !== 'digital-send') {
          this.$zopim.sendChatMsg(responses.transactionIsNotKirimUang);
          return;
        }

        const { data: sendMoneyData } = await getMitraSendMoneyTransaction(this.$axios, invoiceTrx.id);

        if (sendMoneyData.status !== 'remitted') {
          this.$zopim.sendChatMsg(responses.kirimUangStatusIsNotRemitted);
          return;
        }

        const fiveDaysThreshold = 3600 * 24 * 5; // in seconds
        const updatedAtTime = new Date(sendMoneyData.updated_at).getTime();
        const nowTime = new Date().getTime();

        const remittedTimeDiff = Math.floor((nowTime - updatedAtTime) / 1000);

        // Above 5 days
        if (remittedTimeDiff >= fiveDaysThreshold) {
          this.$zopim.sendChatMsg(responses.remittedAbove5Days);
          return;
        }

        // Below 5 days
        this.$zopim.sendChatMsg(responses.remittedBelow5Days);
      } catch (error) {
        this.$zopim.sendChatMsg(responses.error);
        console.error(
          '[ERROR] Something is wrong when trying to check remitted time from digital-send transaction',
          error
        );
      }
    },

    /**
     * Checks kirim uang SLA
     * @command - '[apicall] checkKirimUangSLA [/apicall]'
     * @param {object} currentTrx - currently selected transaction object
     */
    async checkKirimUangSLA(currentTrx) {
      if (!currentTrx) {
        this.$zopim.sendChatMsg(CHATBOT_COMMANDS.responses.noSelectedTransaction);
        return;
      }

      const responses = {
        kirimUangWithinSLA: '[apiresponse] kirimUangWithinSLA [/apiresponse]',
        kirimUangIsStillProcessed: '[apiresponse] kirimUangIsStillProcessed [/apiresponse]',
        kirimUangStatus: '[apiresponse] kirimUangStatus<status> [/apiresponse]',
        kirimUangOutsideSLA: '[apiresponse] kirimUangOutsideSLA [/apiresponse]',
        kirimUangFeatureIsInMaintenance: '[apiresponse] kirimUangFeatureIsInMaintenance [/apiresponse]',
        checkKirimUangSLAError: '[apiresponse] checkKirimUangSLAError [/apiresponse]',
      };

      try {
        /**
         * Checks if the Kirim Uang/Digital Send feature is in maintenance mode
         */
        await this.fetchNeoToggles([DIGITAL_SEND_MAINTENANCE_NEO_TOGGLE_KEY]);
        if (this.getNeoToggles[DIGITAL_SEND_MAINTENANCE_NEO_TOGGLE_KEY]) {
          this.$zopim.sendChatMsg(responses.kirimUangFeatureIsInMaintenance);
          return;
        }

        // Get invoice data
        const {
          data: [invoiceData],
        } = await getInvoices(this.$axios, { keywords: currentTrx.paymentId });
        const {
          transactions: [invoiceTrx],
        } = invoiceData;

        const { data: sendMoneyData } = await getMitraSendMoneyTransaction(this.$axios, invoiceTrx.id);

        const createdAt = new Date(sendMoneyData.created_at);
        const dateNow = new Date();
        const oneDay = 24 * 60 * 60 * 1000;

        const isWithinSLA = dateNow - createdAt < oneDay;

        /**
         * Try to reprocess the transaction if the status is "processed" and is within SLA
         */
        if (sendMoneyData.status === 'processed' && isWithinSLA) {
          const { data: reprocessedTrx } = await updateDigitalSendTransaction(this.$axios, {
            transactionId: sendMoneyData.remote_id,
            userAction: 'reprocess',
          });
          const reprocessedCreatedAt = new Date(reprocessedTrx.created_at);
          const waitDate = reprocessedCreatedAt.setDate(reprocessedCreatedAt.getDate() + 1);

          /**
           * If the reprocessed transaction status is still "processed"
           * The function will send:
           * a special-message
           * and response "[apiresponse] kirimUangIsStillProcessed [/apiresponse]"
           */
          if (reprocessedTrx.status === 'processed') {
            this.sendMessage({
              msg: `Coba tunggu sampai ${humanizeTimestamp(
                waitDate,
                '%dd% %month% %yyyy% jam %hh%:%mi%'
              )} waktu setempat, ya!`,
              nick: 'agent:trigger', // impersonate Acha Bot
              displayName: 'Acha',
            });

            this.$zopim.sendChatMsg(responses.kirimUangIsStillProcessed);
            return;
          }

          /**
           * If the reprocessed transaction status is not "processed" anymore
           * This function will send:
           * response "[apiresponse] kirimUangStatus<status> [/apiresponse]""
           */
          const { status } = reprocessedTrx;
          const capitalizedState = status.charAt(0).toUpperCase() + status.slice(1);
          const finalResponse = responses.kirimUangStatus.replace('<status>', capitalizedState);
          this.$zopim.sendChatMsg(finalResponse);
          return;
        }

        /**
         * If the transaction status is other than "processed" and within SLA
         * This function will send:
         * a special-message
         * and response "[apiresponse] kirimUangWithinSLA [/apiresponse]"
         */
        if (sendMoneyData.status !== 'processed' && isWithinSLA) {
          const waitDate = createdAt.setDate(createdAt.getDate() + 1);
          this.sendMessage({
            msg: `Coba tunggu sampai ${humanizeTimestamp(
              waitDate,
              '%dd% %month% %yyyy% jam %hh%:%mi%'
            )} waktu setempat, ya!`,
            nick: 'agent:trigger', // impersonate Acha Bot
            displayName: 'Acha',
          });

          this.$zopim.sendChatMsg(responses.kirimUangWithinSLA);
          return;
        }

        /**
         * Outside SLA case
         * This function will send:
         * response "[apiresponse] kirimUangOutsideSLA [/apiresponse]"
         */
        this.$zopim.sendChatMsg(responses.kirimUangOutsideSLA);
        return;
      } catch (error) {
        this.$zopim.sendChatMsg(responses.checkKirimUangSLAError);
        console.error('[ERROR] Something is wrong when trying to check user withdrawal status', error);
      }
    },
  },
};
